import { supabaseClient } from './supabase';
import { dedupeEnergyFormatRowsByYear } from './energyFormatRowSelection';
import {
  energyFormatGeographyTypes,
  geofipsVariantsForEnergyFormat,
} from '../utils/energyFormatQuery';

export type GasFormatProvenanceRow = {
  year: number;
  data_source: string;
  measurement: string;
  method_note: string;
};

async function fetchNgFormatRows(
  table: 'ng_res_format' | 'ng_com_format',
  sectorLabel: 'Residential' | 'Commercial',
  geofips: string | number,
  popGeographyType: string | null | undefined,
  years: number[]
): Promise<Record<string, unknown>[]> {
  const geoTypes = energyFormatGeographyTypes(popGeographyType);
  const variants = geofipsVariantsForEnergyFormat(geofips);
  const merged: Record<string, unknown>[] = [];
  for (const gt of geoTypes) {
    for (const gf of variants) {
      const res = await supabaseClient
        .from(table)
        .select('year, data_source, measurement, method_note, total_therms, geography_type, geofips')
        .eq('geofips', gf)
        .eq('geography_type', gt)
        .eq('sector', sectorLabel)
        .in('year', years);
      if (!res.error && res.data?.length) merged.push(...res.data);
    }
  }
  return dedupeEnergyFormatRowsByYear(merged, 'total_therms');
}

export async function fetchGasProvenanceForView(params: {
  geofips: number;
  geographyType: string | null | undefined;
  viewId: 'res' | 'com' | 'total' | 'perCapita';
  latestYear: number | null;
}): Promise<GasFormatProvenanceRow | null> {
  const year = params.latestYear;
  if (year == null) return null;
  const years = [year];
  const resRows = await fetchNgFormatRows(
    'ng_res_format',
    'Residential',
    params.geofips,
    params.geographyType,
    years
  );
  const comRows = await fetchNgFormatRows(
    'ng_com_format',
    'Commercial',
    params.geofips,
    params.geographyType,
    years
  );

  if (params.viewId === 'res') {
    const row = resRows.find((r) => Number(r.year) === year);
    return row
      ? {
          year,
          data_source: String(row.data_source || ''),
          measurement: String(row.measurement || ''),
          method_note: String(row.method_note || ''),
        }
      : null;
  }
  if (params.viewId === 'com') {
    const row = comRows.find((r) => Number(r.year) === year);
    return row
      ? {
          year,
          data_source: String(row.data_source || ''),
          measurement: String(row.measurement || ''),
          method_note: String(row.method_note || ''),
        }
      : null;
  }

  const resRow = resRows.find((r) => Number(r.year) === year);
  const comRow = comRows.find((r) => Number(r.year) === year);
  if (!resRow && !comRow) return null;

  const classify = (m: string): 'observed' | 'modeled' | 'unknown' => {
    const s = m.toLowerCase();
    if (s.includes('observ')) return 'observed';
    if (s.includes('estimat') || s.includes('model') || s.includes('extrapol')) return 'modeled';
    return 'unknown';
  };
  const resClass = resRow ? classify(String(resRow.measurement || '')) : null;
  const comClass = comRow ? classify(String(comRow.measurement || '')) : null;
  let combined = 'MIXED_SOURCE';
  if (resClass === 'observed' && comClass === 'observed') combined = 'OBSERVED';
  else if (resClass === 'modeled' && comClass === 'modeled') combined = 'MODELED';
  else if (
    (resClass === 'observed' && comClass === 'modeled') ||
    (resClass === 'modeled' && comClass === 'observed')
  ) {
    combined = 'MIXED_OBSERVED_MODELED';
  }

  const resSrc = resRow ? String(resRow.data_source || 'residential') : '—';
  const comSrc = comRow ? String(comRow.data_source || 'commercial') : '—';
  return {
    year,
    data_source: `Residential: ${resSrc}; Commercial: ${comSrc}`,
    measurement: combined,
    method_note: [
      resRow ? `Residential: ${String(resRow.measurement || '')} ${String(resRow.method_note || '')}`.trim() : '',
      comRow ? `Commercial: ${String(comRow.measurement || '')} ${String(comRow.method_note || '')}`.trim() : '',
    ]
      .filter(Boolean)
      .join(' | '),
  };
}
